/**
 * Support Resolver — MeesterJay 4.1
 * MOVED from src/utils/supportResolver.ts (Phase L-4)
 */
import { Question } from '../types'
import { VisualAid } from '../visuals/types'
import { HintStep } from './hints'
import {
  FAMILY_SUPPORT_REGISTRY,
  FamilySupportEntry,
} from './familySupportRegistry'
export interface ResolvedSupport {
  hints: HintStep[]
  visual?: VisualAid
  explanation?: string
  source: 'question' | 'family' | 'representation' | 'none'
  // STAGE 3: Support new structured feedback
  solutionSteps?: string[]
}

/**
 * Deterministically resolve support content for a question.
 *
 * Resolution Order:
 * 1. Question-level overrides (if provided in seed)
 * 2. Representation-specific support (if representationId matches)
 * 3. Family-level default support
 * 4. Fallback (empty)
 *
 * STAGE 3: Enhanced to support new feedback structure
 */
export function resolveSupportContent(question: Question): ResolvedSupport {
  const familyId = question.familyId
  const registryEntry = FAMILY_SUPPORT_REGISTRY[familyId]

  // Check for representationId in question data
  const representationId = question.data?.representationId as string | undefined
  const representationSupport =
    representationId && registryEntry?.representations?.[representationId]

  // Resolve Hints
  let hints: HintStep[] = []
  if (representationSupport?.hints) {
    hints = representationSupport.hints
  } else if (registryEntry?.hints) {
    hints = registryEntry.hints
  }

  // STAGE 3: Resolve Visual from canonical supportAids + family fallbacks
  let visual: VisualAid | undefined

  // Check new supportAids structure first
  if (question.supportAids && question.supportAids.length > 0) {
    visual = question.supportAids[0] // Use first support visual
  } else {
    // Family/representation fallback
    if (representationSupport?.visual) {
      visual = representationSupport.visual
    } else if (registryEntry?.defaultVisual) {
      visual = registryEntry.defaultVisual
    }
  }

  // Resolve explanation from canonical question.feedback + registry fallback
  let explanation: string | undefined
  let solutionSteps: string[] | undefined
  if (question.feedback) {
    explanation = question.feedback.explanationText
    solutionSteps = question.feedback.solutionSteps
  } else {
    // Family/representation fallback
    if (representationSupport?.explanation) {
      explanation = representationSupport.explanation
    } else if (registryEntry?.defaultExplanation) {
      explanation = registryEntry.defaultExplanation
    }
  }

  // Determine source for debugging/reporting
  let source: ResolvedSupport['source'] = 'none'
  if (question.supportAids || question.feedback) {
    source = 'question'
  } else if (representationSupport) {
    source = 'representation'
  } else if (registryEntry) {
    source = 'family'
  }
  return {
    hints,
    visual,
    explanation,
    solutionSteps,
    source,
  }
}
